/**
 * Date Utilities - Single Source of Truth for Date Operations
 *
 * This module provides a centralized, type-safe API for all date operations
 * using date-fns. All date manipulations across the application should use
 * these utilities to ensure consistency and maintainability.
 *
 * @module lib/utils/date
 */

import {
  addDays,
  addMonths as dateFnsAddMonths,
  endOfDay as dateFnsEndOfDay,
  endOfMonth as dateFnsEndOfMonth,
  endOfWeek as dateFnsEndOfWeek,
  endOfYear as dateFnsEndOfYear,
  format as dateFnsFormat,
  isSameDay as dateFnsIsSameDay,
  startOfDay as dateFnsStartOfDay,
  startOfMonth as dateFnsStartOfMonth,
  startOfWeek as dateFnsStartOfWeek,
  startOfYear as dateFnsStartOfYear,
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  getDate,
  getDay,
  getMonth,
  getYear,
  isValid,
  isWithinInterval,
  parseISO,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'
import { isDefined } from './commonTypeGuards'

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Options for formatting dates
 */
export interface DateFormatOptions {
  /**
   * Include the year in the formatted output
   * @default false
   */
  includeYear?: boolean

  /**
   * Custom format string (see date-fns format documentation)
   */
  customFormat?: string
}

/**
 * Options for date range formatting
 */
export interface DateRangeFormatOptions extends DateFormatOptions {
  /**
   * Separator between start and end dates
   * @default ' - '
   */
  separator?: string
}

// ============================================================================
// Core Date Creation & Validation
// ============================================================================

/**
 * Creates a new Date object, ensuring it's valid
 *
 * @param value - Date string, timestamp, or Date object
 * @returns Valid Date object
 * @throws Error if the date is invalid
 *
 * @example
 * ```typescript
 * const date = createDate('2025-01-15');
 * const date = createDate(1705276800000);
 * const date = createDate(new Date());
 * ```
 */
export function createDate(value?: string | number | Date): Date {
  const date = isDefined(value) ? new Date(value) : new Date()

  if (!isValid(date)) {
    throw new Error(`Invalid date: ${String(value)}`)
  }

  return date
}

/**
 * Safely checks if a value is a valid Date object
 *
 * @param value - Value to check
 * @returns True if value is a valid Date
 *
 * @example
 * ```typescript
 * isValidDate(new Date()) // true
 * isValidDate('2025-01-15') // false (string, not Date)
 * isValidDate(new Date('invalid')) // false
 * ```
 */
export function isValidDate(value: unknown): value is Date {
  return value instanceof Date && isValid(value)
}

/**
 * Parses an ISO date string to a Date object
 *
 * @param isoString - ISO 8601 date string (e.g., '2025-01-15' or '2025-01-15T10:30:00Z')
 * @returns Parsed Date object
 * @throws Error if the string cannot be parsed
 *
 * @example
 * ```typescript
 * const date = parseDateFromISO('2025-01-15');
 * const dateTime = parseDateFromISO('2025-01-15T10:30:00Z');
 * ```
 */
export function parseDateFromISO(isoString: string): Date {
  const date = parseISO(isoString)

  if (!isValid(date)) {
    throw new Error(`Invalid ISO date string: ${isoString}`)
  }

  return date
}

// ============================================================================
// Date Normalization (Start/End of Day/Week)
// ============================================================================

/**
 * Returns the start of the day (00:00:00.000)
 *
 * @param date - Date to normalize
 * @returns Date set to start of day
 *
 * @example
 * ```typescript
 * const start = startOfDay(new Date('2025-01-15T14:30:00'));
 * // Returns: 2025-01-15T00:00:00.000
 * ```
 */
export function startOfDay(date: Date): Date {
  return dateFnsStartOfDay(date)
}

/**
 * Returns the end of the day (23:59:59.999)
 *
 * @param date - Date to normalize
 * @returns Date set to end of day
 *
 * @example
 * ```typescript
 * const end = endOfDay(new Date('2025-01-15T14:30:00'));
 * // Returns: 2025-01-15T23:59:59.999
 * ```
 */
export function endOfDay(date: Date): Date {
  return dateFnsEndOfDay(date)
}

/**
 * Returns the start of the week (Monday at 00:00:00.000)
 * Week starts on Monday following ISO 8601 standard
 *
 * @param date - Date to find week start for
 * @returns Date representing Monday of that week
 *
 * @example
 * ```typescript
 * const weekStart = startOfWeek(new Date('2025-01-15')); // Wednesday
 * // Returns: 2025-01-13 (Monday) at 00:00:00.000
 * ```
 */
export function startOfWeek(date: Date): Date {
  return dateFnsStartOfWeek(date, { weekStartsOn: 1 }) // Monday
}

/**
 * Returns the end of the week (Sunday at 23:59:59.999)
 * Week ends on Sunday following ISO 8601 standard
 *
 * @param date - Date to find week end for
 * @returns Date representing Sunday of that week
 *
 * @example
 * ```typescript
 * const weekEnd = endOfWeek(new Date('2025-01-15')); // Wednesday
 * // Returns: 2025-01-19 (Sunday) at 23:59:59.999
 * ```
 */
export function endOfWeek(date: Date): Date {
  return dateFnsEndOfWeek(date, { weekStartsOn: 1 }) // Monday start = Sunday end
}

/**
 * Returns the start of the month (1st day at 00:00:00.000)
 *
 * @param date - Date to find month start for
 * @returns Date representing the first day of the month
 *
 * @example
 * ```typescript
 * const monthStart = startOfMonth(new Date('2025-01-15'));
 * // Returns: 2025-01-01 at 00:00:00.000
 * ```
 */
export function startOfMonth(date: Date): Date {
  return dateFnsStartOfMonth(date)
}

/**
 * Returns the end of the month (last day at 23:59:59.999)
 *
 * @param date - Date to find month end for
 * @returns Date representing the last day of the month
 *
 * @example
 * ```typescript
 * const monthEnd = endOfMonth(new Date('2025-01-15'));
 * // Returns: 2025-01-31 at 23:59:59.999
 * ```
 */
export function endOfMonth(date: Date): Date {
  return dateFnsEndOfMonth(date)
}

/**
 * Returns the start of the year (January 1st at 00:00:00.000)
 *
 * @param date - Date to find year start for
 * @returns Date representing January 1st of that year
 *
 * @example
 * ```typescript
 * const yearStart = startOfYear(new Date('2025-06-15'));
 * // Returns: 2025-01-01 at 00:00:00.000
 * ```
 */
export function startOfYear(date: Date): Date {
  return dateFnsStartOfYear(date)
}

/**
 * Returns the end of the year (December 31st at 23:59:59.999)
 *
 * @param date - Date to find year end for
 * @returns Date representing December 31st of that year
 *
 * @example
 * ```typescript
 * const yearEnd = endOfYear(new Date('2025-06-15'));
 * // Returns: 2025-12-31 at 23:59:59.999
 * ```
 */
export function endOfYear(date: Date): Date {
  return dateFnsEndOfYear(date)
}

// ============================================================================
// Date Arithmetic
// ============================================================================

/**
 * Adds the specified number of days to a date
 *
 * @param date - Base date
 * @param amount - Number of days to add (can be negative)
 * @returns New date with days added
 *
 * @example
 * ```typescript
 * const tomorrow = addDaysToDate(new Date(), 1);
 * const yesterday = addDaysToDate(new Date(), -1);
 * ```
 */
export function addDaysToDate(date: Date, amount: number): Date {
  return addDays(date, amount)
}

/**
 * Adds the specified number of months to a date
 *
 * @param date - Base date
 * @param amount - Number of months to add (can be negative)
 * @returns New date with months added
 *
 * @example
 * ```typescript
 * const nextMonth = addMonthsToDate(new Date(), 1);
 * const lastMonth = addMonthsToDate(new Date(), -1);
 * ```
 */
export function addMonthsToDate(date: Date, amount: number): Date {
  return dateFnsAddMonths(date, amount)
}

/**
 * Subtracts the specified number of days from a date
 *
 * @param date - Base date
 * @param amount - Number of days to subtract
 * @returns New date with days subtracted
 *
 * @example
 * ```typescript
 * const lastWeek = subtractDays(new Date(), 7);
 * ```
 */
export function subtractDays(date: Date, amount: number): Date {
  return subDays(date, amount)
}

/**
 * Subtracts the specified number of months from a date
 *
 * @param date - Base date
 * @param amount - Number of months to subtract
 * @returns New date with months subtracted
 *
 * @example
 * ```typescript
 * const lastMonth = subtractMonths(new Date(), 1);
 * const threeMonthsAgo = subtractMonths(new Date(), 3);
 * ```
 */
export function subtractMonths(date: Date, amount: number): Date {
  return subMonths(date, amount)
}

/**
 * Subtracts the specified number of years from a date
 *
 * @param date - Base date
 * @param amount - Number of years to subtract
 * @returns New date with years subtracted
 *
 * @example
 * ```typescript
 * const lastYear = subtractYears(new Date(), 1);
 * ```
 */
export function subtractYears(date: Date, amount: number): Date {
  return subYears(date, amount)
}

// ============================================================================
// Date Comparison
// ============================================================================

/**
 * Checks if two dates represent the same calendar day
 * Ignores time components
 *
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns True if dates are on the same day
 *
 * @example
 * ```typescript
 * const morning = new Date('2025-01-15T08:00:00');
 * const evening = new Date('2025-01-15T20:00:00');
 * isSameDay(morning, evening) // true
 * ```
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return dateFnsIsSameDay(date1, date2)
}

/**
 * Checks if a date falls within a specified interval
 *
 * @param date - Date to check
 * @param start - Start of interval (inclusive)
 * @param end - End of interval (inclusive)
 * @returns True if date is within the interval
 *
 * @example
 * ```typescript
 * const date = new Date('2025-01-15');
 * const start = new Date('2025-01-01');
 * const end = new Date('2025-01-31');
 * isDateInRange(date, start, end) // true
 * ```
 */
export function isDateInRange(date: Date, start: Date, end: Date): boolean {
  return isWithinInterval(date, { start, end })
}

/**
 * Calculates the number of days between two dates
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Absolute number of days between dates
 *
 * @example
 * ```typescript
 * const start = new Date('2025-01-01');
 * const end = new Date('2025-01-08');
 * getDaysDifference(start, end) // 7
 * ```
 */
export function getDaysDifference(startDate: Date, endDate: Date): number {
  return Math.abs(differenceInDays(endDate, startDate))
}

/**
 * Calculates the number of hours between two dates
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Number of hours between dates (can be negative if endDate < startDate)
 *
 * @example
 * ```typescript
 * const start = new Date('2025-01-15T08:00:00');
 * const end = new Date('2025-01-15T14:00:00');
 * getHoursDifference(start, end) // 6
 * ```
 */
export function getHoursDifference(startDate: Date, endDate: Date): number {
  return differenceInHours(endDate, startDate)
}

/**
 * Calculates the number of milliseconds between two dates
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Number of milliseconds between dates
 *
 * @example
 * ```typescript
 * const start = new Date('2025-01-15T08:00:00');
 * const end = new Date('2025-01-15T08:00:01');
 * getMillisecondsDifference(start, end) // 1000
 * ```
 */
export function getMillisecondsDifference(startDate: Date, endDate: Date): number {
  return differenceInMilliseconds(endDate, startDate)
}

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Formats a date to ISO 8601 date string (YYYY-MM-DD)
 *
 * @param date - Date to format
 * @returns ISO date string (e.g., '2025-01-15')
 *
 * @example
 * ```typescript
 * const isoDate = toISODateString(new Date('2025-01-15T14:30:00'));
 * // Returns: '2025-01-15'
 * ```
 */
export function toISODateString(date: Date): string {
  return dateFnsFormat(date, 'yyyy-MM-dd')
}

/**
 * Formats a date to ISO 8601 datetime string
 *
 * @param date - Date to format
 * @returns ISO datetime string (e.g., '2025-01-15T14:30:00.000Z')
 *
 * @example
 * ```typescript
 * const isoDateTime = toISOString(new Date());
 * ```
 */
export function toISOString(date: Date): string {
  return date.toISOString()
}

/**
 * Formats a date for display in the user's locale
 *
 * @param date - Date to format
 * @param options - Formatting options
 * @returns Localized date string
 *
 * @example
 * ```typescript
 * formatDate(new Date('2025-01-15')) // 'Jan 15'
 * formatDate(new Date('2025-01-15'), { includeYear: true }) // 'Jan 15, 2025'
 * formatDate(new Date('2025-01-15'), { customFormat: 'MMMM dd, yyyy' }) // 'January 15, 2025'
 * ```
 */
export function formatDate(date: Date, options: DateFormatOptions = {}): string {
  const { includeYear = false, customFormat } = options

  if (isDefined(customFormat)) {
    return dateFnsFormat(date, customFormat)
  }

  const formatString = includeYear ? 'MMM d, yyyy' : 'MMM d'
  return dateFnsFormat(date, formatString)
}

/**
 * Formats a date range for display
 *
 * @param startDate - Start of range
 * @param endDate - End of range
 * @param options - Formatting options
 * @returns Formatted date range string
 *
 * @example
 * ```typescript
 * const start = new Date('2025-01-15');
 * const end = new Date('2025-01-21');
 * formatDateRange(start, end) // 'Jan 15 - Jan 21'
 * formatDateRange(start, end, { includeYear: true }) // 'Jan 15, 2025 - Jan 21, 2025'
 * ```
 */
export function formatDateRange(
  startDate: Date,
  endDate: Date,
  options: DateRangeFormatOptions = {}
): string {
  const { separator = ' - ', ...formatOptions } = options

  const startStr = formatDate(startDate, formatOptions)
  const endStr = formatDate(endDate, formatOptions)

  return `${startStr}${separator}${endStr}`
}

/**
 * Formats a date range for display with intelligent year handling
 * Only shows years if different from current year
 *
 * @param startDate - Start of range
 * @param endDate - End of range
 * @returns Formatted date range string
 *
 * @example
 * ```typescript
 * // If current year is 2025
 * const start = new Date('2025-01-15');
 * const end = new Date('2025-01-21');
 * formatDateRangeWithYear(start, end) // 'Jan 15 - Jan 21'
 *
 * const crossYear = new Date('2024-12-28');
 * const endYear = new Date('2025-01-03');
 * formatDateRangeWithYear(crossYear, endYear) // 'Dec 28, 2024 - Jan 3'
 * ```
 */
export function formatDateRangeWithYear(startDate: Date, endDate: Date): string {
  const currentYear = getYear(new Date())
  const startYear = getYear(startDate)
  const endYear = getYear(endDate)

  const startNeedsYear = startYear !== currentYear
  const endNeedsYear = endYear !== currentYear

  const startStr = formatDate(startDate, { includeYear: startNeedsYear })
  const endStr = formatDate(endDate, { includeYear: endNeedsYear })

  return `${startStr} - ${endStr}`
}

/**
 * Formats a relative time string (e.g., "2 hours ago", "just now")
 *
 * @param date - Date to format
 * @returns Relative time string
 *
 * @example
 * ```typescript
 * const now = new Date();
 * const twoHoursAgo = subtractDays(now, 0.083);
 * formatRelativeTime(twoHoursAgo) // '2 hours ago'
 *
 * const lastWeek = subtractDays(now, 7);
 * formatRelativeTime(lastWeek) // '7 days ago'
 * ```
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffInMs = getMillisecondsDifference(date, now)
  const diffInHours = diffInMs / (1000 * 60 * 60)
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

  if (diffInHours < 1) {
    return 'Just now'
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours)
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  } else if (diffInDays < 7) {
    const days = Math.floor(diffInDays)
    return `${days} ${days === 1 ? 'day' : 'days'} ago`
  } else {
    return formatDate(date)
  }
}

// ============================================================================
// Date Component Extraction
// ============================================================================

/**
 * Gets the day of the week (0 = Sunday, 6 = Saturday)
 *
 * @param date - Date to extract day from
 * @returns Day of week number (0-6)
 *
 * @example
 * ```typescript
 * const date = new Date('2025-01-15'); // Wednesday
 * getDayOfWeek(date) // 3
 * ```
 */
export function getDayOfWeek(date: Date): number {
  return getDay(date)
}

/**
 * Gets the day of the week in Monday-first format (0 = Monday, 6 = Sunday)
 *
 * @param date - Date to extract day from
 * @returns Day of week number in Monday-first format (0-6)
 *
 * @example
 * ```typescript
 * const monday = new Date('2025-01-13');
 * getDayOfWeekMondayFirst(monday) // 0
 *
 * const sunday = new Date('2025-01-19');
 * getDayOfWeekMondayFirst(sunday) // 6
 * ```
 */
export function getDayOfWeekMondayFirst(date: Date): number {
  const day = getDay(date)
  return day === 0 ? 6 : day - 1
}

/**
 * Gets the abbreviated day name in Monday-first format
 *
 * @param date - Date to get day name for
 * @returns Abbreviated day name ('Mon', 'Tue', etc.)
 *
 * @example
 * ```typescript
 * const date = new Date('2025-01-15'); // Wednesday
 * getDayName(date) // 'Wed'
 * ```
 */
export function getDayName(date: Date): string {
  const mondayFirstNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const index = getDayOfWeekMondayFirst(date)
  return mondayFirstNames[index] ?? 'Unknown'
}

/**
 * Gets the year from a date
 *
 * @param date - Date to extract year from
 * @returns Year number
 *
 * @example
 * ```typescript
 * getYearFromDate(new Date('2025-01-15')) // 2025
 * ```
 */
export function getYearFromDate(date: Date): number {
  return getYear(date)
}

/**
 * Gets the month from a date (0 = January, 11 = December)
 *
 * @param date - Date to extract month from
 * @returns Month number (0-11)
 *
 * @example
 * ```typescript
 * getMonthFromDate(new Date('2025-01-15')) // 0 (January)
 * ```
 */
export function getMonthFromDate(date: Date): number {
  return getMonth(date)
}

/**
 * Gets the day of the month from a date
 *
 * @param date - Date to extract day from
 * @returns Day of month (1-31)
 *
 * @example
 * ```typescript
 * getDayOfMonth(new Date('2025-01-15')) // 15
 * ```
 */
export function getDayOfMonth(date: Date): number {
  return getDate(date)
}

/**
 * Gets the current year
 *
 * @returns Current year number
 *
 * @example
 * ```typescript
 * getCurrentYear() // 2025 (if called in 2025)
 * ```
 */
export function getCurrentYear(): number {
  return getYear(new Date())
}

// ============================================================================
// Week Utilities
// ============================================================================

/**
 * Generates an array of all dates in a week
 *
 * @param weekStart - Start date of the week (typically Monday)
 * @returns Array of 7 dates representing the week
 *
 * @example
 * ```typescript
 * const monday = new Date('2025-01-13');
 * const weekDays = getWeekDays(monday);
 * // Returns: [Mon 13, Tue 14, Wed 15, Thu 16, Fri 17, Sat 18, Sun 19]
 * ```
 */
export function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = []
  for (let i = 0; i < 7; i++) {
    days.push(addDaysToDate(weekStart, i))
  }
  return days
}

/**
 * Formats a week range for display (e.g., "Oct 6 - Oct 12")
 * Automatically includes year if different from current year
 *
 * @param weekStart - Start date of the week
 * @param weekEnd - End date of the week
 * @returns Formatted week range string
 *
 * @example
 * ```typescript
 * const start = new Date('2025-01-13');
 * const end = new Date('2025-01-19');
 * formatWeekRange(start, end) // 'Jan 13 - Jan 19'
 * ```
 */
export function formatWeekRange(weekStart: Date, weekEnd: Date): string {
  return formatDateRangeWithYear(weekStart, weekEnd)
}

// ============================================================================
// Sorting Utilities
// ============================================================================

/**
 * Comparator function for sorting dates in ascending order (oldest first)
 *
 * @param a - First date
 * @param b - Second date
 * @returns Comparison result for Array.sort()
 *
 * @example
 * ```typescript
 * const dates = [new Date('2025-01-15'), new Date('2025-01-10')];
 * dates.sort(compareDatesAsc);
 * // Result: [2025-01-10, 2025-01-15]
 * ```
 */
export function compareDatesAsc(a: Date, b: Date): number {
  return getMillisecondsDifference(a, b)
}

/**
 * Comparator function for sorting dates in descending order (newest first)
 *
 * @param a - First date
 * @param b - Second date
 * @returns Comparison result for Array.sort()
 *
 * @example
 * ```typescript
 * const dates = [new Date('2025-01-10'), new Date('2025-01-15')];
 * dates.sort(compareDatesDesc);
 * // Result: [2025-01-15, 2025-01-10]
 * ```
 */
export function compareDatesDesc(a: Date, b: Date): number {
  return getMillisecondsDifference(b, a)
}

/**
 * Checks if two dates are equal (same timestamp)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if dates represent the exact same moment
 *
 * @example
 * ```typescript
 * const date1 = new Date('2025-01-15T14:30:00');
 * const date2 = new Date('2025-01-15T14:30:00');
 * areDatesEqual(date1, date2) // true
 * ```
 */
export function areDatesEqual(date1: Date, date2: Date): boolean {
  return getMillisecondsDifference(date1, date2) === 0
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Number of milliseconds in common time units
 */
export const TIME_UNITS = {
  MILLISECOND: 1,
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
} as const
